import React from 'react';

import { isDefined } from '@nfctron/nexus-core';
import { UiButton } from '@nfctron/nexus-react';

import { Text } from '@mantine/core';
import Image from 'next/image';

import { useSessionStore } from '@root/features/authentication/stores/sessionStore';

import { RegisterSheet } from '@root/lib/components/RegisterSheet/RegisterSheet';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type Component, ComponentDisplayName } from '@root/lib/types/components';

import { LoginSheet } from '../LoginSheet/LoginSheet';

import { type AccountSheetProps } from './AccountSheet.types';

/**
 * AccountSheet component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const AccountSheet: Component<AccountSheetProps> = ({
	open,
	onClose,
	registerForm,
	canClose = true,
	onRegister,
	onLogin,
	useAs = 'login',
	type = 'base',
}) => {
	const account = useSessionStore((state) => state.account);
	const { t: typeSensitiveT } = useTranslate(APP_I18N_NAMESPACE.COMMON, [AccountSheet.displayName, type]);

	/** sheet state management */
	const [loginSheetOpen, setLoginSheetOpen] = React.useState(false);
	const [registerSheetOpen, setRegisterSheetOpen] = React.useState(false);

	/** handler to set sheet based on the provided type */
	const setSheet = React.useCallback(
		(state: boolean) => {
			if (state) {
				setLoginSheetOpen(useAs === 'login');
				setRegisterSheetOpen(useAs === 'register');
			} else {
				setLoginSheetOpen(false);
				setRegisterSheetOpen(false);
			}
		},
		[useAs],
	);

	/** trigger opening/closing sheet upon external `open` prop change */
	React.useEffect(() => {
		setSheet(open);
	}, [open, setSheet]);

	return (
		<React.Fragment>
			{/* login sheet */}
			<LoginSheet open={loginSheetOpen} onClose={canClose ? onClose : undefined} onSuccess={onLogin}>
				{/* banner card */}
				{!isDefined(account) && (
					<div className="bg-lucky-point-500 flex flex-col items-start rounded-lg p-3">
						<span className="text-base font-semibold text-white">{typeSensitiveT('loginBanner.title')}</span>
						<span className="text-sm text-white/75">{typeSensitiveT('loginBanner.text')}</span>

						{/* register button */}
						<UiButton
							size="sm"
							color="unstyled"
							variant="unstyled"
							className="text-lucky-point-500 mt-2 bg-white hover:bg-white/95"
							onClick={() => {
								setLoginSheetOpen(false);
								setRegisterSheetOpen(true);
							}}
						>
							{typeSensitiveT('loginBanner.button.text')}
						</UiButton>
					</div>
				)}
			</LoginSheet>

			{/* register sheet */}
			<RegisterSheet open={registerSheetOpen} onClose={canClose ? onClose : undefined} formProps={registerForm} onSubmit={onRegister} type={type}>
				{/* banner card */}
				{!isDefined(account) && (
					<div className="bg-lucky-point-500 flex flex-col items-start rounded-lg p-3">
						<span className="text-base font-semibold text-white">{typeSensitiveT('registerBanner.title')}</span>
						<span className="text-sm text-white/75">{typeSensitiveT('registerBanner.text')}</span>

						{/* register button */}
						<UiButton
							size="sm"
							color="unstyled"
							variant="unstyled"
							className="text-lucky-point-500 mt-2 bg-white hover:bg-white/95"
							onClick={() => {
								setRegisterSheetOpen(false);
								setLoginSheetOpen(true);
							}}
						>
							{typeSensitiveT('registerBanner.button.text')}
						</UiButton>
					</div>
				)}
			</RegisterSheet>
		</React.Fragment>
	);
};

/**
 * AccountSheet component display name
 * @type {ComponentDisplayName}
 */
AccountSheet.displayName = 'component/AccountSheet';
